import { z } from "zod";
import { exerciseClip, exerciseTracking } from "../catalog/exercise-list";
import { catalogueLocale, laterality, loadMode, muscleRole, } from "../catalog/vocabulary";
import { MAX_PRIORITY_MUSCLES, MOVEMENT_PATTERNS, PLAN_SPLITS, PRIORITY_MUSCLES, } from "./protocols";
/**
 * Re-exported so the training barrel names it beside `planItemView`, where
 * the deck reads it. It LIVES in `catalog/exercise-list.ts` because the list
 * row and the detail carry the same object and this file already imports from
 * there — the other direction is a cycle that evaluates to `undefined`.
 */
export { exerciseTracking, } from "../catalog/exercise-list";
/**
 * The training plan on the wire — the plan itself, its sessions, its items,
 * the alternatives behind a swap on the deck, and the templates a plan is
 * saved as and applied from (`D119`, `D106`).
 *
 * ── A PLAN HAS SESSIONS NOW, AND A PLAN WITH NONE IS ONE ────────────────────
 * `D119` puts `training_plan_session` between the plan and its items, because
 * the engine needs to say *"Workout A · Monday"* and `workout_session.plan_id`
 * could not say which of them was done. A plan written before migration `0005`
 * has no session rows and is read as ONE implicit session — so `sessions` is
 * never empty on a plan that has items, and a client never has to handle both
 * shapes.
 *
 * ── THE VIEWS ARE JOINED, THE INPUTS ARE IDS ────────────────────────────────
 * `planItemView` carries the exercise's name, muscle, equipment and clip
 * because the deck renders a card per item and a second request per item is
 * the fan-out `T-6` bills for. `planItemInput` carries an `exerciseId` and
 * nothing else, because the server does not trust a client's copy of a
 * catalogue row.
 */
/** The two words `ck_training_plan_status` admits (`training-plan.ts:70`).
 *  A draft is `archived` — `publish()` is what makes one `active`. */
export const PLAN_STATUSES = ["active", "archived"];
export const planStatus = z.enum(PLAN_STATUSES);
const namedRef = z.object({ id: z.int().positive(), name: z.string().min(1) });
/** The catalogue's 1–5 level, one declaration for every row that carries it,
 *  so the picker compares an item and a candidate on the same scale. */
const difficultyLevel = z.int().min(1).max(5);
/**
 * The ceiling on the refusals one publish carries — the aggregate's
 * `MAX_ITEMS_PER_PLAN`, restated because a shared package may not import a
 * module package (the architecture audit's rule 5).
 */
const MAX_PUBLISH_REJECTIONS = 200;
const planItemTargets = {
    /** Every target is nullable: a plan may prescribe an exercise and leave the
     *  numbers to the person, and `null` load is bodyweight and not `0`. */
    notes: z.string().nullable(),
    restSeconds: z.int().positive().nullable(),
    targetLoad: z.number().nonnegative().nullable(),
    /** `D103`'s *"2 × 10–12"* — a range needs two columns, which is why
     *  `target_reps` alone could not hold it. */
    targetRepsMax: z.int().positive().nullable(),
    targetRepsMin: z.int().positive().nullable(),
    targetSets: z.int().positive().nullable(),
};
/**
 * `POST /me/plan/items`.
 *
 * Optional AND nullable on every target: omitted is *"unspecified"* and so is
 * `null`, and both are accepted so a client can build the body from a form
 * without stripping empty fields.
 *
 * ── `prescribe: true` HANDS THE NUMBERS TO THE ENGINE (`D182`) ──────────────
 * The server then writes the targets an engine-proposed item of that exercise
 * carries in week 1 of the plan's protocol. A body that names a target AS
 * WELL is refused rather than merged: which of the two won would be a rule
 * only the server knew, and the person would see numbers nobody chose. An
 * explicit `null` counts as naming one — it says "unspecified", which is a
 * choice too.
 */
const PRESCRIBED_TARGETS = [
    "restSeconds",
    "targetDurationSeconds",
    "targetLoad",
    "targetRepsMax",
    "targetRepsMin",
    "targetSets",
];
export const planItemInput = z
    .strictObject({
    exerciseId: z.string().min(1),
    /** A client-generated UUID (the app mints v7), which makes the add a
     *  REPLAYABLE write (`D194`): the outbox resends it whenever it did not
     *  see the answer, and a replay of the same `id` on the same plan answers
     *  the stored line — removed since or not — and adds nothing. An `id` the
     *  plan does not hold but another row does answers 404
     *  `plan.item_not_found`. Omitted = the server mints it, as before. */
    id: z.uuid().optional(),
    notes: z.string().nullable().optional(),
    planId: z.string().min(1),
    /** Omitted on every body an app sent before `D182`, which keeps its
     *  meaning: the targets are the body's own. */
    prescribe: z.literal(true).optional(),
    /** The engine's eligibility before the add (`D227`): an exercise the
     *  person did in a workout and asked to keep in the plan (`D254`). A
     *  refusal is 422 with the reason as its key — `plan.exercise_*` or
     *  `plan.equipment_missing`. Judged after the replay read, so a resend
     *  of an add that landed still answers its line. Omitted: no verdict,
     *  as every add before it. An older server refuses the key (strict). */
    requireEligible: z.literal(true).optional(),
    restSeconds: z.int().positive().nullable().optional(),
    sessionId: z.string().min(1),
    /** A timed pick's target, in whole seconds (`D151`) — the same declaration
     *  `planItemView` carries. Without it every item RECREATED through this
     *  route lost its duration: a plank came back untimed, with a 200 at every
     *  step and nothing red, and the client had nowhere to put the value. */
    targetDurationSeconds: z.int().positive().nullable().optional(),
    targetLoad: z.number().nonnegative().nullable().optional(),
    targetRepsMax: z.int().positive().nullable().optional(),
    targetRepsMin: z.int().positive().nullable().optional(),
    targetSets: z.int().positive().nullable().optional(),
})
    .superRefine((body, ctx) => {
    if (body.prescribe !== true) {
        return;
    }
    for (const key of PRESCRIBED_TARGETS) {
        if (body[key] !== undefined) {
            ctx.addIssue({
                code: "custom",
                message: "a prescribed item carries no target of its own",
                path: [key],
            });
        }
    }
});
export const planItemCreated = z.object({
    itemId: z.string().min(1),
    /** Position WITHIN its session (`D119`), no longer plan-global. */
    position: z.int().nonnegative(),
});
/**
 * `PUT /me/plan/items/:id/replace` — put another exercise in THIS item's slot
 * (`D43` of the front-end plan; the swipe deck's "keep this one").
 *
 * ── ONE CALL, BECAUSE THREE CALLS HAVE NO TRANSACTION (`T-90`) ──────────────
 * The same edit used to be `DELETE` the item, `POST` the new one, then `PUT`
 * the session's order. A dropped connection after the first left a slot with
 * no exercise; after the second, the new exercise at the END of the session.
 * Every step answered 2xx, so nothing went red. This is one aggregate
 * mutation on the server instead.
 *
 * The answer is the NEW item as a `planItemView`: same `sessionId`, same
 * `position`, the replaced item's sets, rep range, hold, rest and notes,
 * `targetLoad: null` (a load belongs to the exercise it was written for) and
 * `reason: null` (so does the engine's reason). The three older routes stay,
 * for the callers that compose them.
 */
export const planItemReplaceInput = z.strictObject({
    exerciseId: z.string().min(1),
    /** The NEW line's client-generated UUID (`D194`). With it the replace is
     *  replayable: once a line by this id is on the plan, the answer is that
     *  line and nothing is written — the old line already left. Omitted = the
     *  server mints it, and a resend after a lost answer meets 404
     *  `plan.item_not_found`, as before. */
    id: z.uuid().optional(),
    planId: z.string().min(1),
});
/**
 * `POST /me/plan/items/:id/restore` — bring a soft-deleted item back, the
 * undo of a remove or of a replace (`D182`, amending `D53`).
 *
 * Nothing is copied: the row in the path gets its `deletedAt` cleared and
 * keeps its id, session, position, targets (its load included), reason and
 * notes. The answer is that row as a `planItemView`, with the SAME `itemId`.
 *
 * `replacing` is the undo of a replace: a live item of the same plan and the
 * same session, soft-deleted in the same aggregate mutation, so the slot is
 * never empty and never doubled in between (`T-90`). Without it, this is the
 * undo of a remove.
 *
 * A retry is harmless: the row already live, with `replacing` absent or
 * already deleted, answers 200 with the item and writes nothing. 404
 * `plan.item_not_found` when either id is not an item of `planId`; 409
 * `plan.restore_conflict` when `replacing` is live in another session, or
 * both rows are live.
 */
export const planItemRestoreInput = z.strictObject({
    planId: z.string().min(1),
    replacing: z.string().min(1).optional(),
});
/**
 * `PUT /me/plan/items/:id/targets` — the person's own numbers for one line
 * (`D197`): the editor's sets, reps, hold and rest, the server's
 * `retargetPlanItem` (`D191`) behind it.
 *
 * Omitted keeps the line's value and `null` clears it — the same reading as
 * the trainer's `changePlanExercise`. A body that changes nothing is refused
 * `422 plan.retarget_unchanged`, so the app sends only what moved.
 *
 * `id` is the NEW line's client id: an item is immutable, so a change of
 * targets is a new row in the same slot (the replace's shape), and with the
 * id a resend after a lost answer gets that row back rather than a 404 for
 * the line it already replaced.
 */
export const planItemRetargetInput = z.strictObject({
    id: z.uuid().optional(),
    notes: z.string().nullable().optional(),
    planId: z.string().min(1),
    restSeconds: z.int().positive().nullable().optional(),
    targetDurationSeconds: z.int().positive().nullable().optional(),
    targetLoad: z.number().nonnegative().nullable().optional(),
    targetRepsMax: z.int().positive().nullable().optional(),
    targetRepsMin: z.int().positive().nullable().optional(),
    targetSets: z.int().positive().nullable().optional(),
});
/**
 * `PUT /me/plan/items/:id/session` (`D214`): the line to ANOTHER day of the
 * same plan, whole. `id` is the NEW line's client id, which makes it
 * replayable (`D194`); a move to the day it is on is 422
 * `plan.move_unchanged`.
 */
export const planItemMoveInput = z.strictObject({
    id: z.uuid().optional(),
    planId: z.string().min(1),
    sessionId: z.string().min(1),
});
/**
 * Why the engine put THIS exercise here (`D147`) — a key and its parameters,
 * never a sentence, for the same reason `planItemAlternative` gives: the app
 * renders it in the person's language (`D27`), and `reference/reason-keys.md`
 * is the list of keys.
 */
export const planItemReason = z.object({
    key: z.string().min(1),
    params: z.record(z.string(), z.union([z.string(), z.number()])),
});
export const planItemView = z.object({
    ...planItemTargets,
    exercise: z.object({
        clip: exerciseClip.nullable(),
        /**
         * The catalogue's execution tip in the request's locale — the same string
         * `exerciseDetail.coachingTip` carries, so the card and the detail never
         * disagree.
         *
         * `null`: the catalogue has none for this exercise. ABSENT: a server older
         * than the field, and the client then draws no tip box.
         */
        coachingTip: z.string().min(1).nullable().optional(),
        /**
         * The catalogue level, on `slotCandidate`'s scale, so the swap picker can
         * compare the current exercise with a candidate (`D181`). ABSENT from a
         * server older than the field, and the picker then draws no level row.
         */
        difficultyLevel: difficultyLevel.optional(),
        equipment: namedRef,
        /**
         * How a logged load reads (`D196`): what the workout's load field says
         * under the number ("kg por halter", "kg extra", "kg de assistência")
         * and what the device's own volume and record figures read (`D209`).
         * `null` and ABSENT both read as `total` — absent is a server older than
         * the field (`D172`).
         */
        loadMode: loadMode.nullable().optional(),
        /**
         * Whether the sides work in turn (`D232`): a `unilateral` exercise's
         * reps are logged per side, and the workout's reps field says so. `null`
         * and ABSENT are unknown, read as before (`D172`).
         */
        laterality: laterality.nullable().optional(),
        /**
         * EVERY catalogue muscle of the exercise with its role, targets first —
         * the primary included, unlike `secondaryMuscles`. The weekly volume
         * counts a set 1 for each target and 0.5 for each synergist (`D182`).
         *
         * ABSENT from a server older than the field, and the client then counts
         * `primaryMuscle` as the only target. Never read absence as an empty list:
         * an exercise that works no muscle would drop out of every total.
         */
        muscles: z.array(namedRef.extend({ role: muscleRole })).optional(),
        /** Resolved in the REQUEST's locale by the server's three-level fallback.
         *  The client never falls back again — it has nothing left. */
        name: z.string().min(1),
        primaryMuscle: namedRef,
        /**
         * Every OTHER muscle the exercise works — targets before synergists, each
         * group by activation descending: the catalogue detail's own order with
         * the primary removed.
         *
         * ABSENT from a server older than the field, and the client then shows no
         * secondary line at all. An EMPTY array is the other answer: the exercise
         * works one muscle and the server said so.
         */
        secondaryMuscles: z.array(namedRef).optional(),
        /** Optional for the deploy window; absent reads as reps and weight
         *  (`D151`, `exerciseTracking`). */
        tracks: exerciseTracking.optional(),
    }),
    exerciseId: z.string().min(1),
    itemId: z.string().min(1),
    position: z.int().nonnegative(),
    /** `null` on an item placed by hand — only the engine has a reason. */
    reason: planItemReason.nullable().optional(),
    sessionId: z.string().min(1),
    /**
     * The superset the line is performed in (`D246`): consecutive lines of one
     * day sharing this key are done in rounds, the rest after the round. `null`
     * is a line on its own, and so is ABSENT — a server older than the field.
     * The plan's read sends the key only on a line that stands in a group; a
     * one-line answer (a swap, a change, an undo) sends the line's stored key,
     * so a client reads groups through `supersetKeys`, never this field alone.
     */
    supersetId: z.string().min(1).nullable().optional(),
    /** A timed pick's target — 30 s from the engine, progressed by 5 s
     *  (`D151`). `null` where the exercise is counted, not held. */
    targetDurationSeconds: z.int().positive().nullable().optional(),
    /**
     * The load increment the plan's protocol progresses THIS exercise by, in
     * kilograms — the lower-body or the upper-body step, chosen by the same
     * function the engine progresses with, so the scrubber and the next
     * prescription never step differently.
     *
     * ABSENT — never `null`, never a default — when the plan has no protocol (a
     * hand-written plan), and from a server older than the field. The client
     * then uses its own `DEFAULT_LOAD_STEP_KG`. A `.default(2.5)` here would
     * make "no protocol" indistinguishable from "the protocol says 2.5".
     */
    loadStepKg: z.number().positive().optional(),
});
/** A movement pattern (`D212`) — `catalog_exercise.pattern`'s vocabulary. */
export const movementPattern = z.enum(MOVEMENT_PATTERNS);
/** A week's shape (`D213`). */
export const planSplit = z.enum(PLAN_SPLITS);
/** One of the twelve training muscles, by its lowercased English name. */
export const priorityMuscle = z.enum(PRIORITY_MUSCLES);
/** One day of a `custom` week (`D213`): what it is called, what it trains. */
export const planDayInput = z.strictObject({
    focus: z.array(movementPattern).min(1).max(8),
    label: z.string().trim().min(1).max(40),
});
/** One thing the generator could not honour (`D212`): a key and its params. */
export const planFinding = z.object({
    key: z.string().min(1),
    params: z.record(z.string(), z.union([z.string(), z.number()])),
});
/**
 * `days` is the custom split's and nothing else's: a custom week with no days
 * is nothing to build, and days beside a named split would be ignored —
 * refused instead, so a caller never believes they were used.
 */
export function requireDaysWithCustom(value, context) {
    if (value.split === "custom" && value.days === undefined) {
        context.addIssue({
            code: "custom",
            message: "a custom split names its days",
            path: ["days"],
        });
    }
    if (value.split !== "custom" && value.days !== undefined) {
        context.addIssue({
            code: "custom",
            message: "days are only for split: custom",
            path: ["days"],
        });
    }
}
/**
 * The warm-up, computed by the route rather than stored (`D103`): 3 minutes in
 * week 1 and 10 from week 2, with the first compound's ramp-up sets. It is a
 * function of the plan's protocol and the week index, both of which the server
 * knows — a column would be a copy that goes stale the moment the week turns.
 */
export const planSessionWarmup = z.object({
    firstCompoundSets: z.array(z.object({
        /** Percentage of the working load — `50` means half. */
        loadPct: z.int().min(1).max(100),
        reps: z.int().positive(),
    })),
    minutes: z.int().nonnegative(),
});
export const planSessionView = z.object({
    /**
     * The movement patterns the generator built this day as (`D213`) — what a
     * rebuild of the day rebuilds it as. Absent for a day the generator did
     * not build, and from an older server (`D172`).
     */
    focus: z.array(movementPattern).optional(),
    items: z.array(planItemView),
    /**
     * What the workout is CALLED — *"Workout A"*, *"Upper"*, or the engine's
     * bare letter. Display text and nothing more; no client parses it
     * (`use-week.ts`, `split.ts`).
     *
     * WHOSE WORD IT IS STILL DOES NOT TRAVEL. A `renamed` flag beside this
     * field was removed rather than left waiting for a caller, because
     * `training_plan_session` stores no provenance and the server would have
     * had to invent the answer. `0019` added the migration that note said
     * reinstating it would start with — but what it added is `name` below,
     * WHAT the workout works, not a provenance for this one.
     */
    label: z.string().min(1),
    /**
     * What the workout WORKS — *"Peito & Dorsais"*: the two primary muscles it
     * prescribes the most sets for, in the request's locale.
     *
     * Stored (`0019`) and editable through `PUT /me/plan/sessions/:id`, which
     * is why it is a column rather than something the app derives — a name
     * derived on every render cannot be renamed. Where nothing has stored one,
     * the server generates the same string for the response and writes nothing,
     * so every plan published before that migration, and every draft, carries
     * one too.
     *
     * OPTIONAL, NEVER NULL, and absent for a session with no items: a new
     * output field stays optional so an older app parses a newer server
     * (`D172`). An app that has it prints it; an app that does not shows the
     * label it always showed.
     */
    name: z.string().min(1).optional(),
    position: z.int().nonnegative(),
    sessionId: z.string().min(1),
    warmup: planSessionWarmup,
    /** `0` = Sunday … `6` = Saturday, or `null` for a session with no fixed
     *  day — which is every session of a variable week. */
    weekday: z.int().min(0).max(6).nullable(),
});
/**
 * The engine's standing proposal to change the plan (`D150`): a protocol
 * review after the block, or a swap for a plateaued exercise. The app renders
 * it as a card the person accepts or dismisses; nothing changes until then.
 */
export const planSuggestion = z.object({
    /** `null` for a plateau, where the protocol stays and an exercise goes. */
    protocolId: z.string().nullable(),
    reasonKey: z.string().min(1),
    params: z.record(z.string(), z.union([z.string(), z.number()])),
    suggestedAt: z.iso.datetime(),
});
export const planView = z.object({
    createdAt: z.iso.datetime(),
    /**
     * What the generator could not honour when it wrote this plan (`D212`) —
     * a muscle under the weekly floor, a pattern no candidate could fill, a
     * repeat because the pool ran out — each an i18n key with its params, for
     * the draft review to show. Optional: an older server sends none.
     */
    findings: z.array(planFinding).optional(),
    /** `null` for a self-directed plan; the group that holds the pen otherwise
     *  (`D51`). The student can take it back — `planManagingGroup`. */
    managingGroupId: z.string().nullable(),
    /** What the engine could not honour while generating — i18n keys, for the
     *  draft screen to show beside the plan (`D150`). Empty is the usual case. */
    notices: z.array(z.string().min(1)).optional(),
    planId: z.string().min(1),
    /**
     * The number of weeks in one block of the plan's protocol — its deload
     * cadence, the `M` of *"Semana N de M"*. The week `N` is derived from
     * `publishedAt`, as before. `null` for a plan with no protocol (written by
     * hand); absent from a server older than the field.
     */
    blockWeeks: z.int().positive().nullable().optional(),
    /** `null` on a plan written before the engine, or by hand. */
    protocolId: z.string().nullable(),
    /** When this plan went active. `null` on a plan published before the
     *  column existed, whose *"Week N"* keeps counting from `createdAt`. */
    publishedAt: z.iso.datetime().nullable().optional(),
    sessions: z.array(planSessionView),
    /**
     * The week's shape (`D213`) — what the app names instead of a protocol
     * name that says "full body" over upper/lower days. `null` for a plan
     * built by hand; absent from an older server.
     */
    split: planSplit.nullable().optional(),
    status: planStatus,
    /** `null` when the engine has nothing to propose. */
    suggestion: planSuggestion.nullable().optional(),
    updatedAt: z.iso.datetime(),
});
/** `GET /me/plan` — `null` when this account has no plan at all yet. */
export const planOutput = planView.nullable();
/**
 * `?draft=1` asks for the draft instead of the plan in effect.
 *
 * ── `?locale=` IS PART OF THIS QUERY, NOT AN AFTERTHOUGHT ──────────────────
 * `planItemView.exercise.name` is resolved in the REQUEST's locale by the
 * route's three-level fallback, and the route defaults to `en`. This schema
 * carried only `draft` for as long as it existed, so no client could send a
 * locale on the read that fills the week — while `session-rebuild.ts` and
 * `candidate-feed.ts` DID send one on their own routes. A pt-BR account
 * therefore saw an English week, Portuguese names arriving into it from a
 * rebuild, and English again after the next reconcile. Every response is a
 * 200 and every name is a real name, so no gate anywhere can see it: a
 * mixed-language list is not a shape error.
 *
 * Optional because the parameter is, on the wire and in the route. A client
 * that omits it gets the server's `en`, which is what every client did
 * before.
 */
export const planQuery = z.object({
    draft: z.literal("1").optional(),
    locale: catalogueLocale.optional(),
});
/** `POST /me/plan` — a new draft. No body; the strict empty object is what
 *  makes a client that sends one get a 422 rather than silent acceptance. */
export const planCreateInput = z.strictObject({});
export const planCreated = z.object({ planId: z.string().min(1) });
export const planPublishInput = z.strictObject({
    planId: z.string().min(1),
    /**
     * The draft's changes the person refused on the device, applied in the same
     * request as the publish (`D182`) — one call, because a refusal sent on its
     * own and a publish after it have no transaction across them (`T-90`).
     * Each id is a `draftChange.changeId`, echoed and never built.
     *
     * Sent only when non-empty, so a server older than the field never sees it
     * for a draft accepted as proposed: the object is strict, and a key it does
     * not know is a 422.
     */
    rejectChangeIds: z
        .array(z.string().min(1))
        .max(MAX_PUBLISH_REJECTIONS)
        .optional(),
});
export const planPublishOutput = z.object({
    /**
     * `false` when the plan was already the active one. Publishing twice is not
     * an error — a double tap must not archive the plan it just published — and
     * the flag is how a screen knows whether to say anything.
     */
    changed: z.boolean(),
    /** The plan this one replaced, archived by the swap. */
    previousPlanId: z.string().nullable(),
    /**
     * The `rejectChangeIds` the draft's diff did not contain — a change that
     * no longer exists, so there was nothing to refuse. ABSENT from a server
     * older than the field (`D172`).
     */
    skippedChangeIds: z.array(z.string()).optional(),
});
/**
 * ── THE DRAFT WORKFLOW (T32, plan D34) ─────────────────────────────────────
 * `GET /me/plan/draft/diff` — what the account's draft changes against the
 * plan in effect; 404 `plan.no_draft` when there is no draft.
 * `POST /me/plan/draft/changes/:changeId/reject` — the draft's slot goes back
 * to the plan in effect's version (an added line goes away), and the answer
 * is the recomputed diff; 404 `plan.change_not_found` for an unknown id.
 * `DELETE /me/plan/draft` — 204, the draft is discarded (a soft delete);
 * 404 `plan.no_draft` when there is none.
 *
 * Accepting ONE change has no call: whatever is not rejected is accepted by
 * `POST /me/plan/publish`, which is also "accept everything". None of these
 * calls ever generates a draft (`D150`, `T-145`).
 *
 * ── THE ALGORITHM IS THE SERVER'S ──────────────────────────────────────────
 * Sessions pair by position; lines match by exercise first (`changed` when a
 * target differs: sets, rep range, hold, rest or load), then the rest pair by
 * order (`swapped`), and the tails are `added` / `removed`. The server's
 * `draft-diff.ts` is the one statement of it; a client never diffs itself.
 */
export const DRAFT_CHANGE_KINDS = [
    "added",
    "removed",
    "swapped",
    "changed",
];
export const draftChangeKind = z.enum(DRAFT_CHANGE_KINDS);
/** Who created the draft: the engine (`POST /me/plan/generate`), the trainer
 *  (the agent's proposal) or the person by hand (`POST /me/plan`). Set once
 *  at creation — the plan's actor columns are restamped on every write
 *  (`D69`) and cannot say it. */
export const DRAFT_AUTHORS = ["engine", "trainer", "you"];
export const draftAuthor = z.enum(DRAFT_AUTHORS);
export const draftChange = z.object({
    /**
     * Stable across reads and across rejecting OTHER changes:
     * `"add:<draftItemId>" | "rm:<activeItemId>" | "swap:<draftItemId>" |
     * "chg:<draftItemId>"`. Opaque to a client — echo it, never build it.
     */
    changeId: z.string().min(1),
    kind: draftChangeKind,
    sessionLabel: z.string().min(1),
    sessionPosition: z.int().nonnegative(),
    /** The plan in effect's line; `null` for `added`. */
    before: planItemView.nullable(),
    /** The draft's line; `null` for `removed`. */
    after: planItemView.nullable(),
    /** The engine's reason for `after`, when it has one. */
    reason: planItemReason.nullable().optional(),
});
export const draftDiffOutput = z.object({
    draftPlanId: z.string().min(1),
    /** `null` when nothing is in effect — every change is then `added`. */
    activePlanId: z.string().nullable(),
    author: draftAuthor,
    createdAt: z.iso.datetime(),
    summary: z.object({
        added: z.int().nonnegative(),
        removed: z.int().nonnegative(),
        swapped: z.int().nonnegative(),
        changed: z.int().nonnegative(),
    }),
    changes: z.array(draftChange),
});
/**
 * `PUT /me/plan/managing-group` — the student moves the pen to a group, or
 * takes it back with `null` (`D51`).
 *
 * STUDENT-ONLY, and the use case refuses any other actor. A trainer who could
 * call this could hand a student to another group, or take a student from one.
 */
export const planManagingGroupInput = z.strictObject({
    groupId: z.string().min(1).nullable(),
    planId: z.string().min(1),
});
export const planManagingGroupOutput = z.object({
    managingGroupId: z.string().nullable(),
    previousManagingGroupId: z.string().nullable(),
});
/**
 * `POST /me/plan/generate` — the engine's proposal, as a DRAFT.
 *
 * The only field is the protocol, and it is optional: everything else the
 * engine needs (goal, days, minutes, equipment, injuries, level) is already
 * stored, and a body that could override it would be a second source of truth
 * for the plan the person then follows.
 *
 * ── `continueWeeks` IS NOT A WEEK NUMBER, AND THAT IS THE POINT (T19) ───────
 * The isolation-onset review has to be generated for the week the person is
 * actually in, and published without restarting that count. A `week: 5` field
 * would let a client name any week it liked and get a plan built for it — a
 * prescription nobody made, and a `published_at` that could be set anywhere.
 * The boolean asks for the ONE week the server can already prove: the
 * incumbent's own (`propose-plan.ts`, `carryWeeks`).
 */
export const generatePlanInput = z
    .strictObject({
    /** Continue the plan in effect's week count instead of starting at week 1 —
     *  the isolation-onset review (`D150` amendment). Ignored with no plan in
     *  effect. */
    continueWeeks: z.boolean().optional(),
    /** The days of a `custom` week, in order (`D213`). Only with it. */
    days: z.array(planDayInput).min(2).max(6).optional(),
    /**
     * Exercises the engine must not pick — the ones the person swapped away
     * from a previous draft, so a regenerate does not hand them straight back.
     * Bounded because it is a preference list, not a filter over the catalogue.
     */
    excludeExerciseIds: z.array(z.string().min(1)).max(20).optional(),
    /** Muscles to bias the week towards (`D213`), at most three. */
    priorityMuscles: z
        .array(priorityMuscle)
        .max(MAX_PRIORITY_MUSCLES)
        .optional(),
    /** Omitted = the catalogue's default protocol (`protocols.ts`, `isDefault`). */
    protocolId: z.string().min(1).optional(),
    /** The week's shape (`D213`); omitted, the protocol's split applies. */
    split: planSplit.optional(),
})
    .superRefine(requireDaysWithCustom);
/**
 * `POST /me/plan/sessions/:id/regenerate` — the engine rebuilds ONE workout
 * of the plan in effect (`D102`, `D118`).
 *
 * ── THE SESSION IS IN THE PATH; THE BODY IS THE REFUSALS AND NOTHING ELSE ───
 * Which day is being rebuilt is the resource, so it is a path parameter, and
 * the plan is never named at all: it is the ACTIVE one, resolved from the
 * token. A body that could name a plan would let a client rebuild a day of an
 * archived week and get a 200 for a write nobody would ever see.
 *
 * ── WHY THE PROTOCOL IS NOT A FIELD HERE, AND IS ON `generate` ──────────────
 * A protocol is a property of the WEEK. Changing it on one day would leave a
 * plan whose Monday follows one set of numbers and whose Wednesday follows
 * another — a prescription no protocol in the catalogue makes. Changing
 * protocols stays `generate` plus `publish`, which is the door `D150` and
 * `ENGINEERING-BACKLOG.md` already point at.
 */
export const regeneratePlanSessionInput = z.strictObject({
    /**
     * Exercises the engine must not pick for THIS rebuild — *"rebuild this day
     * without that lift"*. The same shape, the same bound and the same meaning
     * as `generatePlanInput.excludeExerciseIds`: a per-request list, UNIONED
     * with the account's stored refusals (`D06`) and never a replacement for
     * them.
     */
    excludeExerciseIds: z.array(z.string().min(1)).max(20).optional(),
});
/**
 * `PUT /me/plan/protocol` — accept the protocol the agent proposed (`D102`).
 *
 * The body names the PLAN, never the protocol: what is applied is the standing
 * `proposed_protocol_id`, so a client cannot smuggle in an id nobody proposed
 * and call it an acceptance. Choosing a protocol outright stays where it
 * always was — the engine's `generate`, and the agent's conversation.
 */
export const planProtocolInput = z.strictObject({
    planId: z.string().min(1),
});
export const planProtocolOutput = z.object({
    /** The protocol now in effect on the plan — the accepted proposal. */
    protocolId: z.string().min(1),
});
/**
 * One swap offered for an item on the deck (`D106`), at most three.
 *
 * ── THE REASON IS A KEY AND PARAMETERS, NOT A SENTENCE ──────────────────────
 * `reasonKey` is an i18n key and `reasonParams` fills its placeholders, so the
 * app renders *"same muscle, no barbell"* in the person's own language.
 * `D27` again: the client chooses the text. A server-composed sentence would
 * be untranslatable and, if it ever came from a model, unverifiable.
 */
export const planItemAlternative = z.object({
    clip: exerciseClip.nullable(),
    difficultyLevel,
    exerciseId: z.string().min(1),
    name: z.string().min(1),
    reasonKey: z.string().min(1),
    reasonParams: z.record(z.string(), z.union([z.string(), z.number()])),
});
/** At most three: a deck card offers a choice, not a catalogue. */
export const MAX_ITEM_ALTERNATIVES = 3;
export const planItemAlternativesOutput = z.object({
    alternatives: z.array(planItemAlternative).max(MAX_ITEM_ALTERNATIVES),
});
/**
 * One candidate for a slot in the day editor — the same shape as
 * `planItemAlternative` plus what a candidate cannot inherit, and a paging
 * identity.
 *
 * ── A SECOND ENDPOINT, NOT A WIDER FIRST ONE ────────────────────────────────
 * `GET /me/plan/items/:id/alternatives` above is untouched and still capped at
 * three: the first-run deck (`D106`) and the session screen's sheet both read
 * it, and *"a deck card offers a choice, not a catalogue"* stays true of them.
 * The editor deals a new candidate on every rejection, so it needs a page, a
 * cursor, and a way to say why the pool ended.
 *
 * ── IT CARRIES ITS OWN EQUIPMENT AND MUSCLE; AN ALTERNATIVE DOES NOT ────────
 * `planItemAlternative` omits both deliberately, because every substitution
 * reason it offers is `same_muscle_*` — an alternative INHERITS the muscle and
 * the equipment of the card it replaces. A candidate chosen in the editor is
 * not necessarily same-muscle-same-equipment, so it has to carry its own or it
 * renders against the wrong card's context.
 *
 * The reason is a key and its parameters and never a sentence, for the same
 * reason `planItemAlternative` gives (`D27`).
 */
export const slotCandidate = z.object({
    clip: exerciseClip.nullable(),
    difficultyLevel,
    /** The equipment's id beside its name — the picker's equipment chips key on
     *  it (`D181`). ABSENT from a server older than the field, and the picker
     *  then offers no equipment filter. */
    equipmentId: z.int().positive().optional(),
    equipmentName: z.string().min(1),
    exerciseId: z.string().min(1),
    name: z.string().min(1),
    primaryMuscle: namedRef,
    reasonKey: z.string().min(1),
    reasonParams: z.record(z.string(), z.union([z.string(), z.number()])),
    /** What a substitute is logged as — a timed candidate must be logged as
     *  time, not reps. The catalogue list's own object; ABSENT reads as reps and
     *  weight (`D151`), as it does there. */
    tracks: exerciseTracking.optional(),
});
/** The ceiling on one page — the editor deals one card at a time and reads
 *  ahead, so a page is a buffer and not a screenful. */
export const MAX_SLOT_CANDIDATES_PER_PAGE = 10;
/**
 * `GET /me/plan/items/:id/candidates?cursor&limit`.
 *
 * `limit` is decoded FROM A STRING, like every other numeric query parameter
 * in this package (`changeLogQuery`), because `z.coerce` is used nowhere here:
 * it turns `""` into `0` and `"abc"` into `NaN`, and a limit that silently
 * became zero deals an empty page that reads as *"nothing else exists"* — the
 * dead end `exhaustedReasonKey` exists to prevent.
 */
export const slotCandidatesQuery = z.object({
    /** Keyset pagination, OPAQUE: the client echoes back the `nextCursor` it was
     *  handed and never looks inside. An offset would be wrong rather than
     *  merely slower, since the pool is re-ranked between requests. */
    cursor: z.string().min(1).optional(),
    limit: z
        .string()
        .regex(/^\d+$/, "expected a positive integer")
        .transform(Number)
        .pipe(z.int().min(1).max(MAX_SLOT_CANDIDATES_PER_PAGE))
        .optional(),
});
/**
 * `GET /me/plan/sessions/:id/candidates?muscleId&cursor&limit&locale` — the
 * ranked feed for ADDING an exercise to a session (`D182`).
 *
 * The slot feed's query plus the muscle, because an added exercise has no
 * slot to inherit one from. The rows are exercises whose TARGET muscles
 * include it, filtered and ranked like the slot feed, minus every exercise
 * the session already shows; the answer is `slotCandidatesOutput`.
 *
 * `muscleId` is decoded from the string a query string carries, like `limit`
 * and for the same reason: `z.coerce` would read `""` as muscle `0` and deal
 * an empty page that says no exercise works it.
 */
export const sessionCandidatesQuery = slotCandidatesQuery.extend({
    muscleId: z
        .string()
        .regex(/^\d+$/, "expected a positive integer id")
        .transform(Number)
        .pipe(z.int().positive()),
});
export const slotCandidatesOutput = z.object({
    /** `null` when the pool is exhausted — the deck breaks the sequence (`D04`). */
    nextCursor: z.string().min(1).nullable(),
    rows: z.array(slotCandidate).max(MAX_SLOT_CANDIDATES_PER_PAGE),
    /**
     * Why the pool ended, when it did — one of
     * `prescription.exhausted.equipment`, `prescription.exhausted.injury` or
     * `prescription.exhausted.catalogue`, each naming what to change. A key and
     * not a sentence (`D27`).
     *
     * IT IS NOT AN ERROR. It is what `D04`'s break-the-sequence moment renders:
     * the editor states the constraint the person could lift instead of
     * dead-ending. A `null` cursor with a `null` reason leaves the deck nothing
     * to say but *"no more"*, which is the dead end.
     */
    exhaustedReasonKey: z.string().min(1).nullable(),
    /**
     * The params `exhaustedReasonKey`'s copy interpolates — `{ movement }` for
     * the equipment and catalogue reasons, `{ region }` for the injury one.
     * `{}` while there is a next page.
     *
     * ⚠️ WITHOUT IT THE REASON RENDERS AS NOTHING. All three sentences carry a
     * placeholder, and the app renders a message whose param is missing as
     * `null` — the line simply vanishes
     * (`apps/mobile/src/lib/i18n/locales/en.ts`, above the three keys). So a key
     * sent alone is not a shorter sentence, it is the dead end
     * `exhaustedReasonKey` exists to prevent.
     *
     * `.optional()` because it is the one field of this schema that arrived
     * after it — the package's rule for every added output field, so an
     * installed app parses a server that predates it. The server always sends
     * it, `{}` included, and `slot-candidates.test.ts` is what holds it to that.
     */
    exhaustedReasonParams: z
        .record(z.string(), z.union([z.string(), z.number()]))
        .optional(),
});
/** `POST /me/plan/restart-weeks` (`D198`): the plan in effect, by id — a
 *  restart aimed at a plan that has since been replaced is refused. */
export const planRestartWeeksInput = z.strictObject({
    planId: z.string().min(1),
});
export const planRestartWeeksOutput = z.object({
    /** The new week anchor: week 1 starts here. */
    publishedAt: z.iso.datetime(),
});
/**
 * The most days one plan holds — `MAX_SESSIONS_PER_PLAN` in the aggregate,
 * restated for the wire so a reorder naming more is refused at the edge.
 */
export const MAX_PLAN_DAYS = 14;
/**
 * `PUT /me/plan/session-order` (`D210`): EVERY visible day of the plan, in
 * the order they are to follow — not a move instruction, for
 * `planSessionUpdateInput.itemIds`'s reason. A list that misses a day, names
 * one twice or names another plan's is refused by the aggregate with
 * `plan.reorder_sessions_incomplete`, never padded.
 *
 * Its own path, not `PUT /me/plan/sessions/:id`: the days are the plan's,
 * not one day's, and `/me/plan/sessions/order` would be read as a day whose
 * id is "order".
 */
export const planSessionsReorderInput = z.strictObject({
    planId: z.string().min(1),
    sessionIds: z.array(z.string().min(1)).min(1).max(MAX_PLAN_DAYS),
});
/** The most lines one superset holds — `MAX_SUPERSET_SIZE` in the aggregate
 *  (`D246`): beyond four the block is a circuit. */
export const MAX_SUPERSET_SIZE = 4;
/**
 * `PUT /me/plan/superset` (`D246`): group lines of ONE day that stand next to
 * each other into a superset (`grouped: true`, two to four lines), or take
 * lines out of theirs (`grouped: false`). 204.
 *
 * A request that finds every line already as asked writes nothing, so the
 * outbox may replay it. Refused, and never padded: `plan.superset_size`,
 * `plan.superset_not_consecutive`, `plan.superset_mixed_days`, and
 * `plan.item_not_found` for an id the plan does not show.
 */
export const planSupersetInput = z.strictObject({
    grouped: z.boolean(),
    itemIds: z.array(z.string().min(1)).min(1).max(MAX_SUPERSET_SIZE),
    planId: z.string().min(1),
});
export const planSessionCreateInput = z.strictObject({
    /** A client-generated UUID, which makes the add REPLAYABLE (`D197`, the
     *  `planItemInput.id` rule): a replay of the same `id` on the same plan
     *  answers the stored day and adds nothing; an `id` this plan does not
     *  show answers 404 `plan.session_not_found`. Omitted = the server mints
     *  it, as before. */
    id: z.uuid().optional(),
    label: z.string().min(1),
    planId: z.string().min(1),
    weekday: z.int().min(0).max(6).nullable().optional(),
});
export const planSessionCreated = z.object({
    position: z.int().nonnegative(),
    sessionId: z.string().min(1),
});
/**
 * The longest workout name, in characters, after trimming.
 *
 * 60, not the conversation title's 80: the longest name the generator writes
 * is about 24 characters (*"Quadríceps & Posteriores"*), and the app prints
 * this inside a 34 px `h2` on a 320 px screen.
 */
export const MAX_PLAN_SESSION_NAME_LENGTH = 60;
/**
 * `PUT /me/plan/sessions/:id`.
 *
 * ── `itemIds` IS EVERY VISIBLE ITEM OF THIS SESSION, IN ORDER ───────────────
 * Not a move instruction. `reorderItems` is per session now (`D119`), and it
 * takes the whole order because a partial one leaves the positions of the
 * items it does not name undefined against the ones it does. A list missing an
 * item the session has is refused by the aggregate, not silently padded.
 */
export const planSessionUpdateInput = z.strictObject({
    itemIds: z.array(z.string().min(1)).optional(),
    label: z.string().min(1).optional(),
    /**
     * The workout's NAME (`0019`) — what it works, not what it is called.
     *
     * Trimmed here, and an EMPTY string survives on purpose: there is no
     * `.min()`, and the route reads a cleared name as "regenerate", storing the
     * default back rather than storing nothing. Omitted leaves it alone.
     *
     * ⚠️ The object is STRICT, so an app that sends this to a server older than
     * `0019` gets a 422 `request.invalid_body` and the outbox quarantines it.
     * The server deploys first and the app sends the field only once the
     * contract accepts it (`D163`, `D172`).
     */
    name: z.string().trim().max(MAX_PLAN_SESSION_NAME_LENGTH).optional(),
    weekday: z.int().min(0).max(6).nullable().optional(),
});
/** `GET /me/templates` — a plan saved to be applied to another plan. */
export const templateView = z.object({
    createdAt: z.iso.datetime(),
    itemCount: z.int().nonnegative(),
    name: z.string().min(1),
    templateId: z.string().min(1),
    updatedAt: z.iso.datetime(),
});
export const templateListOutput = z.object({
    templates: z.array(templateView),
});
export const templateCreateInput = z.strictObject({
    name: z.string().min(1),
    planId: z.string().min(1),
});
export const templateCreated = z.object({
    itemCount: z.int().nonnegative(),
    /**
     * How many item notes were dropped on the way in.
     *
     * A template is shared shape, and a note is about one person — *"careful,
     * left shoulder"*. Stripping is the right behaviour and it is REPORTED
     * rather than done quietly, because the trainer who wrote those notes is the
     * one who needs to know they are not in the template.
     */
    notesStripped: z.int().nonnegative(),
    templateId: z.string().min(1),
});
export const templateRenameInput = z.strictObject({
    name: z.string().min(1),
});
export const TEMPLATE_APPLY_MODES = ["replace", "append"];
export const templateApplyInput = z.strictObject({
    /** Omitted = `replace`, which is what applying a template usually means. */
    mode: z.enum(TEMPLATE_APPLY_MODES).optional(),
    planId: z.string().min(1),
});
export const templateApplyOutput = z.object({
    addedItemIds: z.array(z.string().min(1)),
    removedItemCount: z.int().nonnegative(),
});
//# sourceMappingURL=plan.js.map